export const brandConfig = {
  // Brand Identity
  name: 'AI Buzz Central',
  tagline: 'Your Hub for AI Innovation & Community',
  description: 'Connect with AI enthusiasts, share insights, and stay updated on the latest trends in artificial intelligence.',

  // Logo & Branding
  logo: {
    light: '/buzzcentrallogo.png',
    dark: '/buzzcentrallogo.png',
    icon: '/buzzcentrallogo.png',
  },

  // Color Scheme
  colors: {
    primary: '#1976D2',      // Blue - Tech and trust
    secondary: '#424242',    // Dark gray
    accent: '#FF6B6B',       // Coral red - Energy and excitement
    success: '#4CAF50',      // Green
    warning: '#FFC107',      // Amber
    error: '#F44336',        // Red
    info: '#2196F3',         // Light blue

    // Brand specific colors
    brandPrimary: '#1E88E5',
    brandSecondary: '#00BCD4',
    brandAccent: '#FF4081',

    // Gradients
    gradient: {
      primary: 'linear-gradient(135deg, #1E88E5 0%, #1976D2 100%)',
      accent: 'linear-gradient(135deg, #FF6B6B 0%, #FF4081 100%)',
      success: 'linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)',
    }
  },

  // Social Media
  social: {
    instagram: {
      handle: '@aibuzzcentral_',
      url: 'https://instagram.com/aibuzzcentral_'
    },
    twitter: {
      handle: '@aibuzzcentral',
      url: 'https://twitter.com/aibuzzcentral'
    },
    linkedin: {
      name: 'AI Buzz Central',
      url: 'https://linkedin.com/company/aibuzzcentral'
    },
    youtube: {
      name: 'AI Buzz Central',
      url: 'https://youtube.com/@aibuzzcentral'
    },
    discord: {
      name: 'AI Buzz Central Community',
      url: 'https://discord.gg/aibuzzcentral'
    },
    github: {
      name: 'aibuzzcentral',
      url: 'https://github.com/aibuzzcentral'
    }
  },

  // Contact Information
  contact: {
    email: 'hello@aibuzzcentral.com',
    support: 'support@aibuzzcentral.com',
    business: 'partnerships@aibuzzcentral.com'
  },

  // Features
  features: {
    community: true,
    discussions: true,
    events: true,
    mentorship: true,
    projects: true,
    gamification: true,
    notifications: true,
    darkMode: true,
    realTimeUpdates: true
  },

  // Navigation Items
  navigation: [
    {
      title: 'Home',
      icon: 'mdi-home',
      to: '/',
      description: 'Discover trending AI content'
    },
    {
      title: 'Groups',
      icon: 'mdi-account-group',
      to: '/groups',
      description: 'Join AI communities'
    },
    {
      title: 'Discussions',
      icon: 'mdi-forum',
      to: '/discussions',
      description: 'Engage in conversations'
    },
    {
      title: 'Events',
      icon: 'mdi-calendar',
      to: '/events',
      description: 'Attend AI events & workshops'
    },
    {
      title: 'Leaderboard',
      icon: 'mdi-trophy',
      to: '/leaderboard',
      description: 'Top contributors'
    }
  ],

  // Categories
  categories: {
    groups: [
      { value: 'machine-learning', label: 'Machine Learning', icon: 'mdi-brain' },
      { value: 'deep-learning', label: 'Deep Learning', icon: 'mdi-neural-network' },
      { value: 'nlp', label: 'Natural Language Processing', icon: 'mdi-text' },
      { value: 'computer-vision', label: 'Computer Vision', icon: 'mdi-eye' },
      { value: 'robotics', label: 'Robotics & AI', icon: 'mdi-robot' },
      { value: 'ai-ethics', label: 'AI Ethics', icon: 'mdi-scale-balance' },
      { value: 'generative-ai', label: 'Generative AI', icon: 'mdi-creation' },
      { value: 'research', label: 'AI Research', icon: 'mdi-flask' },
      { value: 'industry', label: 'AI in Industry', icon: 'mdi-factory' },
      { value: 'education', label: 'AI Education', icon: 'mdi-school' },
      { value: 'tools', label: 'AI Tools & Frameworks', icon: 'mdi-wrench' },
      { value: 'general', label: 'General AI', icon: 'mdi-lightbulb' }
    ],
    discussions: [
      { value: 'question', label: 'Question', color: '#2196F3' },
      { value: 'discussion', label: 'Discussion', color: '#9C27B0' },
      { value: 'tutorial', label: 'Tutorial', color: '#4CAF50' },
      { value: 'news', label: 'News', color: '#FF9800' },
      { value: 'showcase', label: 'Showcase', color: '#E91E63' },
      { value: 'resource', label: 'Resource', color: '#00BCD4' }
    ]
  },

  // Gamification
  gamification: {
    pointsPerAction: {
      createDiscussion: 10,
      createPost: 5,
      receiveUpvote: 2,
      giveUpvote: 1,
      createGroup: 20,
      hostEvent: 15,
      attendEvent: 5,
      dailyLogin: 1
    },
    badgeRarities: [
      { value: 'common', label: 'Common', color: '#9E9E9E' },
      { value: 'rare', label: 'Rare', color: '#2196F3' },
      { value: 'epic', label: 'Epic', color: '#9C27B0' },
      { value: 'legendary', label: 'Legendary', color: '#FF9800' }
    ]
  },

  // Default Settings
  defaults: {
    pageSize: 20,
    maxFileSizeMB: 10,
    avatarSizePx: 200,
    coverImageSizePx: 1200,
    timezone: 'UTC'
  },

  // SEO & Metadata
  seo: {
    title: 'AI Buzz Central - Your Hub for AI Innovation',
    description: 'Join the leading AI community. Connect with experts, share insights, attend events, and stay updated on artificial intelligence trends.',
    keywords: ['AI', 'Artificial Intelligence', 'Machine Learning', 'Deep Learning', 'Community', 'Technology', 'Innovation'],
    author: 'AI Buzz Central Team',
    ogImage: '/og-image.png'
  },

  // API Configuration
  api: {
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8010',
    timeout: 30000,
    retries: 3
  }
}

// Helper functions
export const getBrandColor = (colorName: keyof typeof brandConfig.colors) => {
  return brandConfig.colors[colorName]
}

export const getSocialLink = (platform: keyof typeof brandConfig.social) => {
  return brandConfig.social[platform].url
}

export const getCategoryIcon = (category: string, type: 'groups' | 'discussions' = 'groups') => {
  const categories = brandConfig.categories[type]
  const found = categories.find((c: any) => c.value === category)
  return (found as any)?.icon || 'mdi-help-circle'
}

export const getCategoryLabel = (category: string, type: 'groups' | 'discussions' = 'groups') => {
  const categories = brandConfig.categories[type]
  const found = categories.find(c => c.value === category)
  return found?.label || category
}

export default brandConfig
